import { hideModal, modalBodyType } from '@/redux/navigationSlice';
import { selectModalData } from '@/redux/selectors';
import { Grid, Typography } from '@mui/material';
import { Modal } from '@repo/ui';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Lazy-load modal body components to reduce initial bundle
const CancelNewOrderBodyLazy = dynamic(
  () => import('./cancelNewOrderBody').then((mod) => ({ default: mod.CancelNewOrderBody })),
  { loading: () => null },
);

const CancelSuccessFullOrderBodyLazy = dynamic(
  () => import('./cancelSuccessfullOrderBody').then((mod) => ({ default: mod.CancelSuccessFullOrderBody })),
  { loading: () => null },
);

const CloseSessionBodyLazy = dynamic(
  () => import('./closeSessionBody').then((mod) => ({ default: mod.CloseSessionBody })),
  { loading: () => null },
);

const OrderDetailsBodyLazy = dynamic(
  () => import('./orderDetailsBody').then((mod) => ({ default: mod.OrderDetailsBody })),
  {
    loading: () => null,
  },
);

const OrderFunnelBodyLazy = dynamic(
  () => import('./orderFunnelBody').then((mod) => ({ default: mod.OrderFunnelBody })),
  {
    loading: () => null,
  },
);

const SelectedPlanBodyLazy = dynamic(
  () => import('./selectedPlanBody').then((mod) => ({ default: mod.SelectedPlanBody })),
  {
    loading: () => null,
  },
);

const SmsSentBodyLazy = dynamic(() => import('./smsSentBody').then((mod) => ({ default: mod.SmsSentBody })), {
  loading: () => null,
});

const bodySelector = (body: modalBodyType) => {
  switch (body) {
    case 'cancelSuccessfullOrder':
      return <CancelSuccessFullOrderBodyLazy />;
    case 'cancelNewOrder':
      return <CancelNewOrderBodyLazy />;
    case 'selectedPlan':
      return <SelectedPlanBodyLazy />;
    case 'smsSent':
      return <SmsSentBodyLazy />;
    case 'orderFunnel':
      return <OrderFunnelBodyLazy />;
    case 'orderDetails':
      return <OrderDetailsBodyLazy />;
    case 'closeSession':
      return <CloseSessionBodyLazy />;
    default:
      return <></>;
  }
};

/**
 * Renders a Modal component with title and body based on redux.
 *
 * @return {React.JSX.Element} The rendered Modal component.
 */
export const CommerceModal = () => {
  const modalData = useSelector(selectModalData);
  const dispatch = useDispatch();
  const { open, body, title, onClose } = modalData;
  const BodyComponent = bodySelector(body);

  // Memoized close handler to prevent re-creation on every render
  const handleClose = useCallback(() => {
    dispatch(hideModal());
    onClose?.();
  }, [dispatch, onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Grid
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          maxHeight: { xs: '100%', md: '32rem' },
          py: { xs: 2, md: 4 },
          px: { xs: 0, md: 6 },
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Typography variant="h1">{title}</Typography>
        {BodyComponent}
      </Grid>
    </Modal>
  );
};
